import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { match, P } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import { mediaMaxMobile } from '@blade/ui-theme'
import { googleUserExistenceCheckStateAtom } from '@root/features/auth/google'
import {
  initializationStateAtom,
  isInitializationReadyAtom,
  isWebsiteAtom,
  meStateAtom,
  retryInitializationAction,
} from '@root/features/initialization/atoms/common'
import { InitializationState } from '@root/features/initialization/model'
import {
  AUTH_BG,
  AuthInitial,
  AuthLogin,
  AuthRegister,
} from '@root/features/initialization/ui/common'
import { AuthError } from '@root/features/initialization/ui/common/auth-error'
import { AuthOtp } from '@root/features/initialization/ui/common/auth-login/auth-otp'
import { redirectToLastVisitedNonAuthRouteAction } from '@root/features/routing/atoms'
import { storageSessionStateAtom } from '@root/features/secure-storage/atoms'
import {
  InitializeSecureStorageForm,
  UnlockSecureStorageForm,
} from '@root/features/secure-storage/ui'
import { DomUtil, useBreakpoint } from '@root/shared'
import { ApiError } from '@root/shared/api'
import { isTauriOrBrowserLocalAtom } from '@root/shared/core/platform-util/atoms'
import { useTranslation } from '@root/shared/localization'
import { AuthInfoMessage, AuthModal } from '@root/shared/ui'
import { ProfileIcon } from '@root/shared/ui-kit'
import { AppTitleBar } from '@root/widgets/desktop/app-titlebar'
import { StatusBar } from '@root/widgets/layout/desktop-layout/status-bar'

type Props = {
  withTitleBar?: boolean
}

export const AuthPage: React.FC<Props> = ({ withTitleBar }) => {
  const { t } = useTranslation()
  const [googleUserExistenceCheckState] = useAtom(
    googleUserExistenceCheckStateAtom,
  )
  const [meRequestState] = useAtom(meStateAtom)
  const [initializationState] = useAtom(initializationStateAtom)
  const [storageSessionState] = useAtom(storageSessionStateAtom)
  const [isInitializationReady] = useAtom(isInitializationReadyAtom)
  const retry = useAction(retryInitializationAction)

  const [isWebsite] = useAtom(isWebsiteAtom)
  const isMobile = useBreakpoint() === 'mobile'

  const [isTauriOrBrowserLocal] = useAtom(isTauriOrBrowserLocalAtom)
  const redirectToLastVisitedNonAuthRoute = useAction(
    redirectToLastVisitedNonAuthRouteAction,
  )

  useEffect(() => {
    if (isWebsite) {
      redirectToLastVisitedNonAuthRoute()
    }
  }, [isWebsite, redirectToLastVisitedNonAuthRoute])

  useEffect(() => {
    if (isInitializationReady && isTauriOrBrowserLocal) {
      redirectToLastVisitedNonAuthRoute()
    }
  }, [
    isInitializationReady,
    isTauriOrBrowserLocal,
    redirectToLastVisitedNonAuthRoute,
  ])

  const renderContent = () => {
    if (
      AsyncStateN.isLoading(googleUserExistenceCheckState) ||
      AsyncStateN.isLoading(meRequestState) ||
      AsyncStateN.isLoading(storageSessionState)
    ) {
      return (
        <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
          <AuthInfoMessage
            title={t('auth.please-wait')}
            description={t('auth.please-wait-description')}
            variant="loading"
          />
        </AuthModal>
      )
    }

    if (AsyncStateN.isError(storageSessionState)) {
      return (
        <AuthError
          title={t('auth.keychain-locked')}
          description={t('auth.keychain-access-denied')}
          retry={retry}
        />
      )
    }

    if (
      AsyncStateN.isError(meRequestState) &&
      !ApiError.isUnauthorizedOrExpired(meRequestState.error)
    ) {
      return (
        <AuthError
          title={t('initial.loading_error')}
          description={t('common.something-went-wrong')}
          retry={retry}
        />
      )
    }

    return match(initializationState)
      .with(P.instanceOf(InitializationState.Initial), () => <AuthInitial />)
      .with(P.instanceOf(InitializationState.Login), ({ loginMethod }) => (
        <AuthLogin loginMethod={loginMethod} />
      ))
      .with(
        P.instanceOf(InitializationState.Register),
        ({ registrationMethod }) => (
          <AuthRegister registrationMethod={registrationMethod} />
        ),
      )
      .with(P.instanceOf(InitializationState.InitializeStorage), () => (
        <InitializeSecureStorageForm />
      ))
      .with(P.instanceOf(InitializationState.UnlockStorage), () => (
        <UnlockSecureStorageForm />
      ))
      .with(P.instanceOf(InitializationState.Otp), (state) => (
        <AuthOtp loginPayload={state.loginPayload} />
      ))
      .with(P.instanceOf(InitializationState.Ready), () => undefined)
      .with(P.instanceOf(InitializationState.Website), () => undefined)
      .exhaustive()
  }

  return (
    <Root>
      {withTitleBar ? <AppTitleBar /> : undefined}
      <ContentArea>{renderContent()}</ContentArea>
      {isMobile ? undefined : (
        <Bar withPremiumFeed={false} withTrackerAlerts={false} />
      )}
    </Root>
  )
}

const shadowMove = keyframes`
 0% {
    box-shadow: inset -40px 0px 300px 150px var(--appearance-1);
  }
  20% {
    box-shadow: inset 20px 0px 300px 150px var(--appearance-1);
  }
  40% {
    box-shadow: inset 0px 40px 300px 150px var(--appearance-1);
  }
  60% {
    box-shadow: inset 40px -40px 300px 150px var(--appearance-1);
  }
  80% {
    box-shadow: inset 0px 40px 300px 150px var(--appearance-1);
  }
  100% {
    box-shadow: inset -40px 0px 300px 150px var(--appearance-1);
  }
`

const Root = styled.div`
  background-image: ${DomUtil.cssUrl(AUTH_BG)};

  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;

  transition: box-shadow 10s ease;
  animation: ${shadowMove} 20s infinite;

  ${mediaMaxMobile(css`
    animation: none;
    box-shadow: inset 0 0 150px 50px var(--appearance-1);
  `)}
`

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  place-items: center;
  justify-content: center;
`

const Bar = styled(StatusBar)`
  padding-inline: 8px;
`
